import { Tabs } from '@mui/material';
import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { DemographicsContext } from '../../../global/models/demographics-model';
import { TcsmStates } from '../../../root/tcsm-model';
import { AccountInfoRoutes } from '../../account-info/constants/routes';
import { Completed, Coverage, InProgress, NotStarted, PendingSubmission, PersonalInfo, Relationships, Review } from '../../arc/constants/constant-strings';
import { AnnualEnrollmentProps } from '../../arc/models/arc-epi-props';
import { AnnualEnrollmentRoutes } from '../constants/annual-enrollment-routes';
import { AESteps, AnnualEnrollmentState } from '../models/annual-enrollment-models';
import { progressStyles } from '../../../global/constants/theme/styles/components/progress-tracker';
import { ProgressBarStep, ProgressBarStepItem } from '../../../global/components/progress-bar-step';
import { setAEStep } from '../annual-enrollment-slice';
import { buttonStyles } from '../../../global/constants/theme/styles/components/buttonstyles';

const steps = [AESteps.PersonalInfo, AESteps.Relationships, AESteps.Coverage, AESteps.Review];

interface ProgressProps {
	basePath: string;
	skipDisabled?: boolean;
}

function AnnualEnrollmentProgressBar(props: ProgressProps) {
	const app = useContext(DemographicsContext);
	const aeState = useSelector<TcsmStates, AnnualEnrollmentState>(state => state.annualEnrollment[app]);
	const annualEnrollmentSettings = useSelector<TcsmStates, AnnualEnrollmentProps>(state => state.epiProps?.annualEnrollmentSettingsState?.epiContent);
	const location = useLocation();
	const classes = progressStyles();
	const currentStep = aeState?.currentStep;
	const stepIndex = steps.indexOf(currentStep);
	const dispatch = useDispatch();
	const buttonClasses = buttonStyles();
	const history = useHistory();
	if (!location.pathname.includes(props.basePath) || !aeState?.AEInfoLoaded || stepIndex === -1 || location.pathname.includes(AnnualEnrollmentRoutes.Confirmation) || location.pathname.includes(AnnualEnrollmentRoutes.HeldInReview)) return null;

	const getProgress = (idx: number, current: number) => {
		if (idx === current) return annualEnrollmentSettings?.inProgressStepLabel || InProgress;

		const completedStepLabel = idx === steps.indexOf(AESteps.Coverage)
			? (annualEnrollmentSettings?.pendingSubmissionStepLabel || PendingSubmission)
			: (annualEnrollmentSettings?.completedStepLabel || Completed);

		return idx > current ? (annualEnrollmentSettings?.notStartedStepLabel || NotStarted) : completedStepLabel;
	};
	const getStepId = (idx: number, current: number) => {
		if (idx === current) return ProgressBarStep.INPROGRESS;
		return idx > current ? ProgressBarStep.NOTSTARTED : ProgressBarStep.COMPLETED;
	};

	const showLink = (idx: number, current: number) => {
		if (props?.skipDisabled) {
			return props?.skipDisabled;
		}
		return idx < current;
	};

	const personalInfoPath = `${props.basePath}${AccountInfoRoutes.PersonalInfoSegment}`;
	const relationShipPath = `${props.basePath}${AccountInfoRoutes.RelationshipsSegment}`;
	const coveragePath = `${props.basePath}${AnnualEnrollmentRoutes.Coverage}`;


	const getStepNameNode = (name: string, link: string, step?: AESteps) => {
		if (link && step) {
			return (
				<button className={buttonClasses.linkButton} type='button' onClick={() => {
					dispatch(setAEStep(step, app));
					history.push(link);
				}}>
					<p><strong>{name}</strong></p>
				</button>
			);
		}


		if (link) {
			return (
				<Link to={link}>
					<p><strong>{name}</strong></p>
				</Link>
			);
		}

		return (
			<p><strong>{name}</strong></p>
		);
	};

	return (
		<div className={classes.progressContainer}>
			<Tabs
				variant="scrollable"
				scrollButtons
				className='generic-stepper-bar'
			>
				<ProgressBarStepItem
					stepNameNode={getStepNameNode(annualEnrollmentSettings?.personalInformationStepBarName || PersonalInfo, showLink(0, stepIndex) && personalInfoPath)}
					progress={getProgress(0, stepIndex)}
					stepId={getStepId(0, stepIndex)}
				/>
				<ProgressBarStepItem
					stepNameNode={getStepNameNode(annualEnrollmentSettings?.relationshipStepBarName || Relationships, showLink(1, stepIndex) && relationShipPath)}
					progress={getProgress(1, stepIndex)}
					stepId={getStepId(1, stepIndex)}
				/>
				<ProgressBarStepItem
					stepNameNode={getStepNameNode(annualEnrollmentSettings?.coverageStepBarName || Coverage, showLink(2, stepIndex) && coveragePath, AESteps.Coverage)}
					progress={getProgress(2, stepIndex)}
					stepId={getStepId(2, stepIndex)}
				/>
				<ProgressBarStepItem
					stepNameNode={getStepNameNode(annualEnrollmentSettings?.reviewStepBarName || Review, showLink(3, stepIndex) && coveragePath, AESteps.Review)}
					progress={getProgress(3, stepIndex)}
					stepId={getStepId(3, stepIndex)}
				/>
			</Tabs>
		</div>
	);
}

AnnualEnrollmentProgressBar.defaultProps = {
	skipDisabled: false,
};

export default AnnualEnrollmentProgressBar;


